import React, { useReducer, useRef, useEffect, Fragment } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { reducer } from './store/reducer.js';
import { createRangeState } from './store/createRangeState.js';
import { createGradeState } from './store/createGradeState.js';
import { ACTION_TYPES } from './store/actionTypes.js';
import { VehicleCard } from '../vehicle-card/VehicleCard.jsx';
import { VehicleConfigurator } from '../vehicle-configurator/VehicleConfigurator.jsx';
import { RangeFilters } from './RangeFilters.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { VehicleDetails } from './VehicleDetails.jsx';
import { RangeCarousel } from '../range-carousel/RangeCarousel.jsx';
import { NextSteps } from './NextSteps.jsx';
import { VideoPlayer } from '_ui-core-js/React/components/VideoPlayer.jsx';
import { detectBrowser } from '_ui-core-js/utilities/detectBrowser.js';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { RANGE_GTM_TRACKING_VALUES } from '../../../../../range/ui/constants/analytics.js';

export function RangeSelector(props) {

  const {useGrades, introText, hideFilters } = props;

  const [state, dispatch] = useReducer(
    reducer,
    props,
    useGrades ? createGradeState : createRangeState,
  );

  const block = 'c-range-selector';
  const activeModelIndex = state.selectedModel.index;
  const activeModelData = state.models[activeModelIndex];
  const { modelName, description, ccisImgConfig, nextSteps, baseModelName } = activeModelData.displayCard;
  const configuratorData = activeModelData.configurator;
  const imgRotateOn = state.configuratorActive ? 'drag' : 'trigger';
  const configuratorOpenRef = useRef(false);
  const isSafariBrowserRef = useRef(detectBrowser() === 'Apple Safari');
  const playTrackingOverride = {
    name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
    componentName: RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
    componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
    action: RANGE_GTM_TRACKING_VALUES.action.selectModelVideo,
    label: useGrades ? baseModelName : modelName
  }

  function openConfigurator(index, e) {
    e.preventDefault();
    dispatch({
      type: ACTION_TYPES.setConfiguratorActiveStatus,
      configuratorActive: true
    });

    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: useGrades ? RANGE_GTM_TRACKING_VALUES.componentName.gradeRange + '-' + baseModelName : RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: useGrades ?  RANGE_GTM_TRACKING_VALUES.action.selectGrade : RANGE_GTM_TRACKING_VALUES.action.selectModel,
      label: modelName
    })

    if (Number.isInteger(index)) {
      const carouselIndex = index;
      const modelIndex = state.filters[state.selectedModel.filterClassificationId].indexes[index];
  
      dispatch({
        type: ACTION_TYPES.setActiveModelIndex,
        activeIndex: modelIndex,
        carouselIndex: carouselIndex
      });
    }
  };

  function closeConfigurator() {
    dispatch({
      type: ACTION_TYPES.setConfiguratorActiveStatus,
      configuratorActive: false,
    });
  };

  function configuratorInteractionUpdate(imageProps, activeSelections) {
    dispatch({
      type: ACTION_TYPES.updateImageProps,
      imageProps
    });
    dispatch({
      type: ACTION_TYPES.updateConfiguratorActiveSelections,
      activeSelections
    });  
  };

  function carouselMove(index) {
    const modelIndex = state.filters[state.selectedModel.filterClassificationId].indexes[index];
    const carouselIndex = index;

    dispatch({
      type: ACTION_TYPES.setActiveModelIndex,
      activeIndex: modelIndex,
      carouselIndex: carouselIndex
    });
  };

  function updateFilterSelection(classificationId) {
    dispatch({
      type: ACTION_TYPES.updateFilterSelection,
      classificationId
    });
  };

  useEffect(() => {
    configuratorOpenRef.current = state.configuratorActive;
  }, [state.configuratorActive]);

  const shouldRenderCCIS = () => {
    return state.selectedModel.carouselIndex === modelIndex
  }

  function handleOnClick (link) {
    if (_.find(link.dataAttributes, {key:'onClickAction'})) {
      const onClickAction = _.find(link.dataAttributes, {key:'onClickAction'})
      let onClickFunction = _.noop();
  
      if (onClickAction?.value) {
        onClickFunction = props.onClickActions[onClickAction.value]
      }
      onClickFunction(link);
    }
  }

  return (
    <div className={
      classNames({
        [`${block}`]: true,
        [`${block}__configurator-active`]: state.configuratorActive,
        'utils__section-padding-small--uniform': true,
        'safari-browser': isSafariBrowserRef.current
      })}
    >
      <div className='container-xxl'>

        { introText ?  
            <section className={`${block}__content-grid container-xl`}>
              <header className={`${block}__intro-header`}>
                  <h2 className={`${block}__intro-heading`}>
                      {introText.heading}
                  </h2>
              </header>
              <div>
                  <div className={`${block}__grid__content`}>
                      <p>{introText.copy}</p>
                  </div>
              </div>
            </section>
          : 
          <h3 className={`${block}__title`}>{state.title}</h3>
        }
        {!hideFilters && 
          <RangeFilters
          filtersData={state.filters}
          activeClassificationId={state.selectedModel.filterClassificationId}
          updateFilterSelectionCallback={updateFilterSelection}
          openConfiguratorCallback={e => openConfigurator(state.selectedModel.carouselIndex, e)}
          closeConfiguratorCallback={closeConfigurator}
          toggleViewDependency={state.configuratorActive}
          showToggle={!useGrades}
          useGrades={!!useGrades}
          modelName={modelName}
          baseModelName={baseModelName}
          />
        }

        <div className={`${block}__container row light-theme-lighter`}>
          <VehicleDetails 
          block={block}
          configuratorActive={state.configuratorActive}
          modelName={modelName}
          modelBaseName={ useGrades ? baseModelName : modelName}
          description={description}
          />
     
          <RangeCarousel
          key={`range-selector`}
          showControls={true}
          activeSlideIndex={state.selectedModel.carouselIndex}
          externalControl={true}
          zoomView={state.configuratorActive}
          onMoveCallback={index => carouselMove(index)}
          resetDependencies={state.selectedModel.filterClassificationId}
          moveDelay={configuratorOpenRef.current !== state.configuratorActive ? 800 : 0}
          >
            {
              state.filters[state.selectedModel.filterClassificationId].indexes.map((modelIndex, index) => (
                (!state.configuratorActive || (state.configuratorActive && state.models[modelIndex].displayCard.retailModel))  ?
                  <VehicleCard
                  key={state.models[modelIndex].modelName}
                  {...state.models[modelIndex].displayCard}
                  buttonOneOnClickCallback={e => openConfigurator(index, e)}
                  buttonTwoOnClickCallback={e => {
                    fireGTMTrackingEvent({
                      name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
                      componentName: RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
                      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                      action: CORE_GTM_TRACKING_VALUES.action.clickCta,
                      label: RANGE_GTM_TRACKING_VALUES.label.viewCarDetails + '-' + modelName
                    })
                  }}
                  imgRotateOn={imgRotateOn}
                  imgRotationResetEventName={'range-carousel-move'}
                  trackingId={'range-card'}
                  baseModelName={baseModelName}
                  renderSpin={shouldRenderCCIS}
                  /> 
                  :
                  <VideoPlayer {...state.models[modelIndex].displayCard.video} playTrackingOverride={playTrackingOverride}/>
              ))
            }
          </RangeCarousel>
          {
            state.configuratorActive && (
              <Fragment>
                {
                  configuratorData && 
                    <VehicleConfigurator
                    configuratorData={configuratorData}
                    imageProps={ccisImgConfig}
                    modelName={modelName}
                    baseModelName={useGrades ? baseModelName : undefined}
                    onChangeCallback={configuratorInteractionUpdate}
                    />
                }
                <NextSteps 
                block={block} 
                {...nextSteps}
                modelName={modelName}
                />
              </Fragment>
            )
          }

          {
            props.links && props.links.length &&
            <div className={`${block}__ctas`}>
              {
                props.links.map((link) => 
                  <div className={`${block}__button`} key={`${block}-${link.id}`}>
                    <Button {...{
                      id: `${link.id}`,
                      label : link.label,
                      testId : `${link.id}`,
                      type: link.type.toLowerCase(),
                      cssAnimation: false,
                      onClickCallback : () => {
                        handleOnClick(link)
                      }, 
                    }}/>
                  </div>
                )
              }
            </div>
          }
        </div>
      </div>
    </div>
  );
};